<script setup lang="ts">
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { TicketRouteName } from '@/modules/ticket/routes'
import { useTicketDepartmentListQuery } from '@/queries/ticket-department'
import { useUserStore } from '@/stores/user'
import { useRouteQuery } from '@vueuse/router'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const { data } = useTicketDepartmentListQuery({
    sort: ref([{ column: 'priority_number', direction: 'asc' }]),
})
const routeTitle = useRouteQuery('title', 'All tickets')

const departments = computed(() => data.value?.data ?? [])

const defaultSort = [
    { column: 'status_priority', direction: 'asc' },
    { column: 'priority_number', direction: 'asc' },
    { column: 'updated_at', direction: 'desc' },
]
const priorityFirstSort = [
    { column: 'priority_number', direction: 'asc' },
    { column: 'status_priority', direction: 'asc' },
    { column: 'updated_at', direction: 'desc' },
]
const defaultView = 'flat'

function buildQuery(title: string, filters: Array<Record<string, string>>) {
    const sort =
        title === 'My tickets' || title === 'Triage tickets' ? priorityFirstSort : defaultSort
    return {
        name: TicketRouteName.Index,
        query: {
            title,
            'tickets-filter': JSON.stringify(filters),
            'tickets-sort': JSON.stringify(sort),
            'tickets-view': defaultView,
        },
    }
}

const staticLinks = computed(() => [
    {
        key: 'triage',
        label: 'Triage',
        titleQuery: 'Triage tickets',
        to: buildQuery('Triage tickets', [{ column: 'triage', operator: '=', value: 'true' }]),
    },
    {
        key: 'my',
        label: 'My tickets',
        titleQuery: 'My tickets',
        to: buildQuery('My tickets', [
            { column: 'status_name', operator: '!=', value: 'closed' },
            { column: 'assignee_uuid', operator: '=', value: user.value?.uuid || '' },
        ]),
    },
])

const departmentLinks = computed(() =>
    departments.value.map((dept: any) => ({
        key: `dept-${dept.uuid}`,
        label: dept.title,
        titleQuery: `${dept.title} tickets`,
        to: buildQuery(`${dept.title} tickets`, [
            { column: 'status_name', operator: '!=', value: 'closed' },
            { column: 'department_uuid', operator: '=', value: dept.uuid },
        ]),
    }))
)

const allLink = computed(() => ({
    key: 'all',
    label: 'All',
    titleQuery: 'All tickets',
    to: buildQuery('All tickets', [{ column: 'status_name', operator: '!=', value: 'closed' }]),
}))

const ticketLinks = computed(() => [...staticLinks.value, ...departmentLinks.value, allLink.value])

const isActiveLink = (linkTitle: string) => {
    return routeTitle.value === linkTitle
}
</script>

<template>
    <AccordionItem value="tickets" class="border-b-0">
        <AccordionTrigger
            class="py-2 px-4 text-white hover:no-underline hover:bg-neutral-600/50 data-[state=open]:bg-zinc-800"
        >
            <div class="flex items-center">
                <div class="w-6">
                    <FontAwesomeIcon :icon="['fal', 'clipboard-list']" class="size-4!" />
                </div>
                <div class="ml-3 text-xs">Tickets</div>
            </div>
        </AccordionTrigger>
        <AccordionContent class="pb-0 text-xs">
            <div class="flex flex-col text-white">
                <RouterLink
                    v-for="link in ticketLinks"
                    :key="link.key"
                    :to="link.to"
                    class="pl-[52px] py-2 hover:bg-neutral-600/50"
                    :class="{ active: isActiveLink(link.titleQuery) }"
                >
                    {{ link.label }}
                </RouterLink>
            </div>
        </AccordionContent>
    </AccordionItem>
</template>

<style scoped>
.active {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
